import { FC, useEffect, useMemo, useState } from 'react';
import { Alert, Avatar, AvatarGroup, Box, Drawer, IconButton, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';

import type { Trip, TripBuddy } from '../../../../../types/trip.types';
import type { IdeaStop } from '../../../../../types/ideaStop.types';
import { useGetIdeaStopQuery } from '../../../../../store/services/ideasApi';
import { useGetTripsQuery, useMapIdeaStopToTripStopMutation } from '../../../../../store/services/tripsApi';
import { formatTripDuration, formatDateRangeWithYear } from '../../../../../utils/dateUtils';
import { buildAvatarUrl, generateInitials, generateInitialsFromFullName } from '../../../../../utils/avatarUtils';
import {
  buildSendIdeaStopToTripBody,
  getMissingSendToTripFields,
  parseSendIdeaStopToTripError,
  TRIP_CONVERSION_MISSING_FIELD_LABELS,
} from '../../../../../utils/sendIdeaStopToTripPayload';
import { useIsMobile } from '../../../../../hooks/useWindowWidth';
import CustomButton from '../../../../../common/CustomButton';
import CustomInput from '../../../../../common/CustomInput';
import { DateIcon, MarkerPinIcon } from '../../../../../components/Icons';
import tripMockDetailsFallback from '../../../../../assets/images/TripMockDetails.jpg';

import styles from '../../../Discover/components/SaveFlightModal/SaveFlightModal.module.scss';

const isTripActive = (trip: Trip): boolean => {
  if (!trip.start_date || !trip.end_date) return false;
  const now = new Date();
  const start = new Date(trip.start_date);
  const end = new Date(trip.end_date);
  return start <= now && end >= now;
};

export interface SendIdeaStopToTripModalProps {
  open: boolean;
  onClose: () => void;
  ideaStopId: number;
  onSuccess?: (tripName: string) => void;
}

const SendIdeaStopToTripModal: FC<SendIdeaStopToTripModalProps> = ({ open, onClose, ideaStopId, onSuccess }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [listSearch, setListSearch] = useState('');
  const [selectedTripId, setSelectedTripId] = useState<number | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const { data: trips = [] } = useGetTripsQuery({});
  const {
    data: ideaStop,
    isLoading: isStopLoading,
    isError: isStopError,
  } = useGetIdeaStopQuery(String(ideaStopId), {
    skip: !open || !ideaStopId,
  });
  const [mapIdeaStop, { isLoading: isMapping }] = useMapIdeaStopToTripStopMutation();

  useEffect(() => {
    if (!open) {
      setListSearch('');
      setSelectedTripId(null);
      setApiError(null);
    }
  }, [open]);

  useEffect(() => {
    setApiError(null);
  }, [ideaStopId]);

  const filteredTrips = useMemo(() => {
    const q = listSearch.trim().toLowerCase();
    if (!q) return trips;
    return trips.filter(t => t.name.toLowerCase().includes(q));
  }, [trips, listSearch]);

  const missingKeys = useMemo(
    () => (ideaStop ? getMissingSendToTripFields(ideaStop as IdeaStop) : []),
    [ideaStop]
  );

  const renderTripBuddies = (buddies: TripBuddy[] | undefined) => {
    const list = buddies ?? [];
    if (list.length === 0) return null;

    return (
      <AvatarGroup
        max={4}
        className={styles.avatarGroup}
      >
        {list.map(buddy => {
          const avatarUrl = buildAvatarUrl(buddy.profile_picture);
          const hasSplitOrLogin = Boolean(
            buddy.first_name?.trim() || buddy.last_name?.trim() || buddy.email?.trim() || buddy.username?.trim()
          );
          const hasFullName = Boolean(buddy.full_name?.trim());
          const initials = hasSplitOrLogin
            ? generateInitials(buddy.first_name, buddy.last_name, buddy.email, buddy.username)
            : hasFullName
              ? generateInitialsFromFullName(buddy.full_name, buddy.email)
              : `U${String(buddy.id).slice(-1)}`;

          return (
            <Avatar
              key={buddy.id}
              sx={{
                backgroundColor: avatarUrl ? 'transparent' : '#e3f2fd',
                color: avatarUrl ? 'transparent' : '#0098f0',
                fontSize: '10px',
                fontWeight: 600,
              }}
              src={avatarUrl || undefined}
              slotProps={{
                img: {
                  style: {
                    objectFit: 'cover',
                    objectPosition: 'center',
                  },
                },
              }}
            >
              {!avatarUrl && initials}
            </Avatar>
          );
        })}
      </AvatarGroup>
    );
  };

  const handleSave = async () => {
    if (!ideaStop || selectedTripId == null) return;
    const stop = ideaStop as IdeaStop;
    if (getMissingSendToTripFields(stop).length > 0) return;
    const body = buildSendIdeaStopToTripBody(stop, {});
    try {
      setApiError(null);
      await mapIdeaStop({ tripId: selectedTripId, ideaStopId, body }).unwrap();
      const trip = trips.find(t => t.id === selectedTripId);
      onSuccess?.(trip?.name ?? 'Trip');
      onClose();
    } catch (e) {
      setApiError(parseSendIdeaStopToTripError(e));
    }
  };

  const backdropSx = { backgroundColor: 'rgba(34, 43, 58, 0.35)' };
  const titleId = 'send-idea-stop-to-trip-title';

  const saveDisabled =
    trips.length === 0 ||
    !ideaStop ||
    isStopLoading ||
    isStopError ||
    selectedTripId == null ||
    isMapping ||
    missingKeys.length > 0;

  const dialogContent = (
    <>
      <Box className={styles.header}>
        <Typography
          id={titleId}
          variant="h5Bold"
          className={styles.title}
        >
          Send to Trip
        </Typography>
        <IconButton
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Close"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className={styles.scrollBody}>
        {isStopLoading ? (
          <Typography
            variant="h11"
            sx={{ color: '#5E6A81', py: 2 }}
          >
            Loading stop details…
          </Typography>
        ) : isStopError || !ideaStop ? (
          <Alert severity="error">Could not load this idea stop.</Alert>
        ) : (
          <>
            {missingKeys.length > 0 ? (
              <Alert
                severity="warning"
                sx={{ mb: 2, flexShrink: 0 }}
              >
                <Typography
                  variant="h11"
                  sx={{ mb: 1, fontWeight: 600, color: 'inherit' }}
                >
                  This stop is missing data the trip needs (for example start and end times). Open the idea, edit this
                  stop, fill the missing fields, save, then try sending to the trip again.
                </Typography>
                <Box
                  component="ul"
                  sx={{ m: 0, pl: 2.5, mb: 2 }}
                >
                  {missingKeys.map(key => (
                    <Typography
                      key={key}
                      component="li"
                      variant="h11"
                      sx={{ color: 'inherit' }}
                    >
                      {TRIP_CONVERSION_MISSING_FIELD_LABELS[key] ?? key}
                    </Typography>
                  ))}
                </Box>
                {ideaStop.idea != null ? (
                  <CustomButton
                    variant="contained"
                    btnType="button"
                    sx={{ padding: '10px 26px' }}
                    onClick={() => {
                      navigate(`/ideas/${ideaStop.idea}`);
                      onClose();
                    }}
                  >
                    Open idea
                  </CustomButton>
                ) : null}
              </Alert>
            ) : null}

            <Box className={styles.listPanelTop}>
              <Typography
                variant="h9Bold"
                className={styles.sectionLabel}
              >
                Trips
              </Typography>

              <CustomInput
                inputName="send-to-trip-search"
                inputLabel=""
                placeholder="Search"
                value={listSearch}
                onChange={e => setListSearch(e.target.value)}
                customClass={styles.search}
                startIcon={<SearchIcon sx={{ color: '#5e6a81', fontSize: 22 }} />}
              />
            </Box>

            <Box className={styles.listScroll}>
              {trips.length === 0 ? (
                <Box sx={{ py: 2 }}>
                  <Typography
                    variant="h11"
                    sx={{ color: '#5E6A81', mb: 2 }}
                  >
                    You don&apos;t have any trips yet. Create a trip first, then you can send this stop from your idea.
                  </Typography>
                  <CustomButton
                    variant="contained"
                    btnType="button"
                    onClick={() => {
                      navigate('/trips');
                      onClose();
                    }}
                  >
                    Go to My Trips
                  </CustomButton>
                </Box>
              ) : filteredTrips.length === 0 ? (
                <Typography
                  component="p"
                  className={styles.metaText}
                  sx={{ py: 2 }}
                >
                  No trips match your search.
                </Typography>
              ) : (
                <Box className={styles.list}>
                  {filteredTrips.map((trip: Trip) => {
                    const active = isTripActive(trip);
                    const destinations = trip.destination_countries?.map(c => c.name).join(' - ') || '—';

                    return (
                      <button
                        key={trip.id}
                        type="button"
                        className={`${styles.ideaRow} ${selectedTripId === trip.id ? styles.ideaRowSelected : ''}`}
                        onClick={() => setSelectedTripId(trip.id)}
                      >
                        <Box
                          className={styles.thumb}
                          sx={{
                            backgroundImage: `url(${trip.custom_cover || tripMockDetailsFallback})`,
                          }}
                        />
                        <Box className={styles.tripContent}>
                          <Box className={styles.tripTitleRow}>
                            <p className={styles.tripTitleText}>{trip.name}</p>
                            {trip.start_date && trip.end_date && !isMobile ? (
                              <span className={styles.durationMuted}>
                                · {formatTripDuration(trip.start_date, trip.end_date)}
                              </span>
                            ) : null}
                            {active ? (
                              <Box className={styles.activeChip}>
                                <span className={styles.activeChipText}>Active</span>
                              </Box>
                            ) : null}
                          </Box>
                          <Box className={styles.tripDetailsCol}>
                            <Box className={styles.metaLine}>
                              <DateIcon size={16} />
                              <span className={styles.metaText}>
                                {trip.start_date && trip.end_date
                                  ? formatDateRangeWithYear(trip.start_date, trip.end_date)
                                  : '—'}
                              </span>
                            </Box>
                            <Box className={styles.tripLocationRow}>
                              <Box className={styles.metaLine}>
                                <MarkerPinIcon size={16} />
                                <span className={styles.metaText}>{destinations}</span>
                              </Box>
                              {renderTripBuddies(trip.buddies)}
                            </Box>
                          </Box>
                        </Box>
                      </button>
                    );
                  })}
                </Box>
              )}
            </Box>

            {apiError ? (
              <Alert
                severity="error"
                sx={{ mt: 1, flexShrink: 0 }}
              >
                {apiError}
              </Alert>
            ) : null}
          </>
        )}
      </Box>

      <Box className={styles.footer}>
        <CustomButton
          variant="outlined"
          btnType="button"
          onClick={onClose}
          disabled={isMapping}
          customClass={styles.footerBtn}
        >
          Cancel
        </CustomButton>
        <CustomButton
          variant="contained"
          btnType="button"
          onClick={() => void handleSave()}
          disabled={saveDisabled}
          customClass={styles.footerBtn}
        >
          {isMapping ? 'Saving…' : 'Save'}
        </CustomButton>
      </Box>
    </>
  );

  return (
    <>
      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={onClose}
          aria-labelledby={titleId}
          slotProps={{
            backdrop: { sx: backdropSx },
          }}
          PaperProps={{
            className: styles.drawerPaper,
            elevation: 8,
          }}
        >
          {dialogContent}
        </Drawer>
      ) : (
        <Modal
          open={open}
          onClose={onClose}
          aria-labelledby={titleId}
          sx={{
            '& .MuiBackdrop-root': backdropSx,
          }}
        >
          <Box className={styles.modalPaper}>{dialogContent}</Box>
        </Modal>
      )}
    </>
  );
};

export default SendIdeaStopToTripModal;
